import { useEffect, useRef, useState, useCallback } from "react";

interface UseInfiniteScrollOptions<T> {
  fetchFn: (page: number) => Promise<T[]>;
  pageSize?: number;
  threshold?: number;
  enabled?: boolean;
  deps?: any[];
}

export const useInfiniteScroll = <T>({
  fetchFn,
  pageSize = 10,
  threshold = 1.0,
  enabled = true,
  deps = [],
}: UseInfiniteScrollOptions<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || !enabled) return;

    setLoading(true);
    try {
      const newItems = (await fetchFnRef.current(page)) ?? [];

      setData((prev) => (page === 1 ? newItems : [...prev, ...newItems]));

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setPage((prev) => prev + 1);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, hasMore, loading, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const initFetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const newItems = (await fetchFnRef.current(1)) ?? [];
        setData(newItems);
        setPage(2);
        setHasMore(newItems.length >= pageSize);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    initFetch();
  }, [enabled, pageSize, ...deps]);

  useEffect(() => {
    if (!enabled || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: "200px", threshold },
    );

    const node = loaderRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [loadMore, hasMore, loading, enabled, threshold]);

  const reset = useCallback(async () => {
    setPage(1);
    setHasMore(true);
    setData([]);
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const newItems = (await fetchFnRef.current(1)) ?? [];
      setData(newItems);
      setPage(2);
      setHasMore(newItems.length >= pageSize);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled, pageSize]);

  return { data, loading, error, hasMore, loaderRef, reset };
};
